import { getEngravableInsets, normalizeShape } from './tagShapeOrientation';

/** Laser engrave fill — most tags burn through to a dark layer */
const DEFAULT_ENGRAVE_COLOR = '#1a1a1a';

/** Materials known to use non-black engraving (extend as needed) */
const ENGRAVE_COLOR_BY_MATERIAL_ID = {
  // e.g. 17: '#ffffff',
};

const LINE_TIER_SCALE = [1, 0.82, 0.68];
const LINE_HEIGHT_RATIO = 1.15;
const LINE_GAP_RATIO = 0.14;
// Slightly conservative average glyph-width ratio (line 1 renders bold, and
// custom fonts vary) — a bit more margin here means the nowrap/overflow:hidden
// safety net in TagPreview rarely has to actually clip anything.
const CHAR_WIDTH_RATIO = 0.58;

function parseHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const normalized = hex.replace('#', '').trim();
  if (normalized.length === 3) {
    return normalized.split('').map((c) => parseInt(c + c, 16));
  }
  if (normalized.length !== 6) return null;
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

/** Relative luminance 0–1 (sRGB) */
export function getRelativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function getEngraveTextColor(material) {
  if (material?.engraveColor) return material.engraveColor;
  const materialId = material?.materialId ?? material?.id?.replace?.(/^mat-/, '');
  if (materialId && ENGRAVE_COLOR_BY_MATERIAL_ID[materialId]) {
    return ENGRAVE_COLOR_BY_MATERIAL_ID[materialId];
  }
  return DEFAULT_ENGRAVE_COLOR;
}

/** Debossed laser-engrave look on silicone */
export function getEngraveTextStyle(material) {
  const color = getEngraveTextColor(material);
  const surfaceLum = getRelativeLuminance(material?.colorHex);
  const engraveLum = getRelativeLuminance(color);
  const darkEngraveOnLight = engraveLum < surfaceLum;

  if (darkEngraveOnLight) {
    return {
      color,
      textShadow:
        '0 1px 0 rgba(255,255,255,0.35), 0 -1px 2px rgba(0,0,0,0.55), inset 0 0 0 transparent',
    };
  }

  return {
    color,
    textShadow:
      '0 -1px 0 rgba(0,0,0,0.45), 0 1px 2px rgba(255,255,255,0.25)',
  };
}

export function getMaterialSurfaceStyle(material) {
  const hex = material?.colorHex;
  if (!hex) {
    return {
      background: '#ffffff',
      border: '3px solid #1f2937',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.8), 0 4px 12px rgba(0,0,0,0.12)',
    };
  }

  return {
    background: hex,
    border: 'none',
    boxShadow:
      '0 4px 14px rgba(0,0,0,0.18), inset 0 2px 6px rgba(255,255,255,0.25), inset 0 -3px 8px rgba(0,0,0,0.12)',
  };
}

export function hasMaterialAppearance(material) {
  return Boolean(material?.colorHex);
}

/**
 * Font sizes (px) for up to three engraved lines inside the shape's engravable inset.
 * Scales down for long lines so text stays within the face.
 */
export function computeEngraveFontSizesPx(shape, tagWidth, tagHeight, lines, fontScale = 1) {
  const normalized = normalizeShape(shape);
  const insets = getEngravableInsets(normalized);
  const engravableW = tagWidth * (1 - (insets.left + insets.right) / 100);
  const engravableH = tagHeight * (1 - (insets.top + insets.bottom) / 100);
  const scale = typeof fontScale === 'number' && fontScale > 0 ? fontScale : 1;

  const active = lines.filter((line) => line?.text?.trim());
  const lineCount = active.length;
  const result = { line1: null, line2: null, line3: null };

  if (lineCount === 0) {
    const emptySlot =
      engravableH / (3 + 2 * LINE_GAP_RATIO) / LINE_HEIGHT_RATIO;
    result.line1 = Math.round(Math.min(emptySlot * LINE_TIER_SCALE[0] * scale, 36 * scale));
    result.line2 = Math.round(Math.min(emptySlot * LINE_TIER_SCALE[1] * scale, 28 * scale));
    result.line3 = Math.round(Math.min(emptySlot * LINE_TIER_SCALE[2] * scale, 22 * scale));
    return result;
  }

  const gapCount = Math.max(lineCount - 1, 0);
  const slotHeight =
    (engravableH * 0.94) / (lineCount + gapCount * LINE_GAP_RATIO);
  const baseFont = slotHeight / LINE_HEIGHT_RATIO;

  active.forEach((line, index) => {
    const tier = line.tier ?? index;
    const tierScale = LINE_TIER_SCALE[tier] ?? LINE_TIER_SCALE[2];
    let fontSize = baseFont * tierScale * scale;

    const charCount = Math.max(line.text.trim().length, 1);
    const widthLimit = engravableW / (charCount * CHAR_WIDTH_RATIO);
    fontSize = Math.min(fontSize, widthLimit);

    const key = `line${tier + 1}`;
    result[key] = Math.round(Math.max(9, Math.min(fontSize, 44 * scale)));
  });

  return result;
}
